import {
  type StudySwimlaneSelectDto,
  type StudyTaskReadinessSelectDto,
  type StudyTaskSelectDto,
  SwimlaneMeasuresStatus,
} from '@@types/api.types';

export type MeiaTaskColumnStatus = 'complete' | 'empty' | 'partial';
export type MeiaTaskReadinessStatus = MeiaTaskColumnStatus;

export const combineMeiaReadinessStatuses = (statuses: MeiaTaskReadinessStatus[]): MeiaTaskReadinessStatus => {
  if (statuses.includes('empty')) return 'empty';
  if (statuses.includes('partial')) return 'partial';
  return 'complete';
};

export const getMeiaSwimlaneMeasuresReadinessStatus = (status?: SwimlaneMeasuresStatus): MeiaTaskReadinessStatus => {
  if (status === SwimlaneMeasuresStatus.COMPLETE) return 'complete';
  if (status === SwimlaneMeasuresStatus.PARTIAL) return 'partial';
  return 'empty';
};

export const getMeiaSwimlaneMeasuresCollectionStatus = (
  swimlanes: Pick<StudySwimlaneSelectDto, 'measuresStatus'>[],
): MeiaTaskReadinessStatus => {
  if (swimlanes.length === 0) return 'empty';
  return combineMeiaReadinessStatuses(
    swimlanes.map((swimlane) => getMeiaSwimlaneMeasuresReadinessStatus(swimlane.measuresStatus)),
  );
};

export const getMeiaTaskReadinessStatus = (
  readiness: Pick<StudyTaskReadinessSelectDto, 'moeCount' | 'mopAggregation' | 'mopCount' | 'mosCount' | 'scoringRole'>,
): Exclude<MeiaTaskReadinessStatus, 'empty'> => {
  const hasMoE = readiness.moeCount > 0;
  const hasMoP = readiness.mopCount > 0;
  const hasMoS = readiness.mosCount > 0;

  return hasMoE && hasMoP && hasMoS && readiness.mopAggregation && readiness.scoringRole ? 'complete' : 'partial';
};

export const getMeiaTaskReadinessCollectionStatus = (
  readiness: Pick<
    StudyTaskReadinessSelectDto,
    'moeCount' | 'mopAggregation' | 'mopCount' | 'mosCount' | 'scoringRole'
  >[],
): MeiaTaskReadinessStatus => {
  if (readiness.length === 0) return 'empty';

  return readiness.every((entry) => getMeiaTaskReadinessStatus(entry) === 'complete') ? 'complete' : 'partial';
};

type GetMeiaSwimlaneTaskCollectionStatusParams = {
  swimlanes: Pick<StudySwimlaneSelectDto, 'id'>[];
  taskReadiness: StudyTaskReadinessSelectDto[];
};

export const getMeiaSwimlaneTaskCollectionStatus = (
  params: GetMeiaSwimlaneTaskCollectionStatusParams,
): MeiaTaskReadinessStatus => {
  const { swimlanes, taskReadiness } = params;
  if (swimlanes.length === 0) return 'empty';

  const taskReadinessBySwimlaneId = new Map<string, StudyTaskReadinessSelectDto[]>();
  for (const entry of taskReadiness) {
    const entries = taskReadinessBySwimlaneId.get(entry.swimlaneId) ?? [];
    entries.push(entry);
    taskReadinessBySwimlaneId.set(entry.swimlaneId, entries);
  }

  return combineMeiaReadinessStatuses(
    swimlanes.map((swimlane) => getMeiaTaskReadinessCollectionStatus(taskReadinessBySwimlaneId.get(swimlane.id) ?? [])),
  );
};

type GetMeiaTaskColumnStatusParams = {
  swimlaneMeasuresStatus?: SwimlaneMeasuresStatus;
  taskMoeCountsByTaskId: Map<string, number>;
  taskMopCountsByTaskId: Map<string, number>;
  taskMosCountsByTaskId: Map<string, number>;
  tasks: StudyTaskSelectDto[];
};

export const getMeiaTaskColumnStatus = (params: GetMeiaTaskColumnStatusParams): MeiaTaskColumnStatus => {
  const { swimlaneMeasuresStatus, taskMoeCountsByTaskId, taskMopCountsByTaskId, taskMosCountsByTaskId, tasks } = params;

  const measuresStatus = getMeiaSwimlaneMeasuresReadinessStatus(swimlaneMeasuresStatus);
  const taskStatus = getMeiaTaskReadinessCollectionStatus(
    tasks.map((task) => ({
      moeCount: taskMoeCountsByTaskId.get(task.id) ?? 0,
      mopAggregation: task.mopAggregation,
      mopCount: taskMopCountsByTaskId.get(task.id) ?? 0,
      mosCount: taskMosCountsByTaskId.get(task.id) ?? 0,
      scoringRole: task.scoringRole,
    })),
  );

  return combineMeiaReadinessStatuses([measuresStatus, taskStatus]);
};
